import {
  Autocomplete,
  Button,
  Box,
  Card,
  InputAdornment,
  OutlinedInput,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  useTheme,
  CircularProgress,
  Chip,
} from '@mui/material';
import { shouldForwardProp } from '@mui/system';
import { visuallyHidden } from '@mui/utils';
import getAxiosInstance from 'api';
import moment from 'moment';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  border: '1px solid #DEDEDE',
  color: '#212120',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: '500',
}));

const OutlineInputStyle = styled(OutlinedInput, { shouldForwardProp })(({ theme }) => ({
  width: 350,
  marginLeft: 16,
  paddingLeft: 16,
  paddingRight: 16,
  borderRadius: '8px',
  border: '1px solid rgba(33, 33, 32, 0.10)',
  background: '#FFFFFF',
  '& input': {
    background: 'transparent !important',
    paddingLeft: '4px !important',
  },
  [theme.breakpoints.down('lg')]: {
    width: 250,
  },
  [theme.breakpoints.down('md')]: {
    width: '100%',
    marginLeft: 4,
    background: theme.palette.mode === 'dark' ? theme.palette.dark[800] : '#fff',
  },
}));

const colorChip = {
  Created: { color: '#FF8743', background: '#FFF3EC' },
  Pending: { color: '#FFDD14', background: '#FFFEE0' },
  'In Progress': { color: '#FFDD14', background: '#FFFEE0' },
  Active: { color: '#1CB110', background: '#E8F7E7' },
  InActive: { color: '#1CB110', background: '#E8F7E7' },
  Verified: { color: '#1CB110', background: '#E8F7E7' },
  Rejected: { color: '#F44336', background: '#FDECEA' },
  Failed: { color: '#F44336', background: '#FDECEA' },
  Deleted: { color: '#F44336', background: '#FDECEA' },
};

const headCells = [
  { id: 'referenceId', numeric: false, disablePadding: false, label: 'Consent ID' },
  { id: 'fundType', numeric: false, disablePadding: false, label: 'Origination' },
  { id: 'uploadType', numeric: false, disablePadding: false, label: 'Submission Type' },
  { id: 'bankName', numeric: true, disablePadding: false, label: 'Bank Name' },
  { id: 'createdAt', numeric: true, disablePadding: false, label: 'Date' },
  { id: 'accountNumber', numeric: true, disablePadding: false, label: 'Account Number' },
  { id: 'status', numeric: true, disablePadding: false, label: 'Journey Status' },
];

const statusType = [
  { label: 'All', value: '' },
  { label: 'Active', value: 'Active' },
  { label: 'Pending', value: 'In Progress' },
  { label: 'Created', value: 'Created' },
  { label: 'Deleted', value: 'Deleted' },
  { label: 'Failed', value: 'Failed' },
];

const StyledTableCellHead = styled(TableCell)(({ theme }) => ({
  backgroundColor: '#F0F0F0',
  color: 'rgba(33, 33, 32, 0.60)',
  border: '1px solid #DEDEDE',
}));
const rejectionReasonMap = {
  'pdf is unsupported. kindly upload a new pdf.': 'New Format',

  'pdf is an image. kindly upload a new pdf.': 'Image Based',

  'pdf is password-protected. kindly upload a new pdf.': 'Password Protected',

  'request to http://192.168.0.5:8080/extract-transactions-with-for-loop/ failed, reason: socket hang up':
    'Kindly Reupload Bank Statement After 5 minutes',

  'cannot convert float nan to integer':
    'Bank statement is processing',

  'connect econnrefused':
    'Kindly Re-upload Bank Statement After 5 minutes or check back after 12 hours',

  'an unexpected error occurred. please contact support.':
    'Bank Statement is processing you will be notified',

  'bank statement is >3 years.':
    'Bank Statement More than >3 years. Please upload Bank statement within <3 years',

  'error while uploading transactions':
    'Kindly Re-upload Bank Statement After 5 minutes or check back after 12 hours',

  'failed - internal server error':
    'Bank statement is processing',

  'consent expired. please contact support':
    'Number of PDF pages is too large, Try again after sometime',
};
const getRejectionReasonLabel = (reason) => {
  if (!reason) return '';

  const normalizedReason = reason.trim().toLowerCase();

  for (const [errorText, customerMessage] of Object.entries(
    rejectionReasonMap
  )) {
    if (normalizedReason.includes(errorText.toLowerCase())) {
      return customerMessage;
    }
  }

  return reason;
};

const productHeadCell = { id: 'product', numeric: false, disablePadding: false, label: 'Product' };

function EnhancedTableHead({ order, orderBy, onRequestSort, showProduct }) {
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  const visibleCells = showProduct
    ? [headCells[0], productHeadCell, ...headCells.slice(2)]
    : headCells;

  return (
    <TableHead>
      <TableRow>
        {visibleCells.map((headCell) => (
          <StyledTableCellHead
            key={headCell.id}
            sortDirection={orderBy === headCell.id ? order : undefined}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order.toLowerCase() : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </StyledTableCellHead>
        ))}
      </TableRow>
    </TableHead>
  );
}

const Logs = ({ selectedDate, businessPartnerId, showProduct = false }) => {
  const theme = useTheme();
  const { id: paramId } = useParams();

  const id = businessPartnerId || paramId;
  const [order, setOrder] = useState('DESC');
  const [orderBy, setOrderBy] = useState('createdAt');
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [dense] = useState(false);
  const [limit, setLimit] = useState(10);
  const [anchorEl, setAnchorEl] = useState(null);
  const [apiList, setApiList] = useState([]);
  const [total, setTotal] = useState();
  const [selectedLogType, setSelectedLogType] = useState(statusType[0]);
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState({ status: statusType[0] });
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  useEffect(() => {
    setLogs([]);
    fetchData();
  }, [page, limit, filter, order, orderBy, selectedDate]);

  const fetchData = () => {
    setLoading(true);
    const url = 'getConsentLogsByBusinessPartner';
    console.log('page + 1', page + 1);
    getAxiosInstance(
      `adminOrchestrator/dashboard/${url}`,
      'post',
      {
        startDate: moment(selectedDate[0]).format('MM-DD-YYYY'),
        endDate: moment(selectedDate[1]).format('MM-DD-YYYY'),
        businessPartnerId: id,
        page: page + 1,
        limit: limit,
        filter: {
          status: filter.status?.value || statusType[0].value,
        },
        sortBy: orderBy,
        sortDirection: order,
      },
      { 'journey-type': 'Dashboard' }
    )
      .then((res) => {
        if (res?.data?.codes === 200) {
          setLoading(false);
          setLogs(res?.data?.data?.items);
          setTotal(res?.data?.data?.total);
        }
      })
      .catch((err) => {
        setLoading(false);
        setLogs([]);
        setTotal(0);
        toast.error(err?.response?.data?.message || 'Error fetching data');
      });
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'ASC';
    setOrder(isAsc ? 'DESC' : 'ASC');
    setOrderBy(property);
  };

  const isSelected = () => { };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setLimit(event.target.value);
  };

  const handleFilterChange = (key, newValue) => {
    setFilterLoading(true);
    setFilter({ ...filter, [key]: newValue || statusType[0] });
    setTimeout(() => {
      setFilterLoading(false);
    }, 300);
  };

  const handleDownloadExcel = async () => {
    try {
      setDownloadLoading(true);
      // API returns JSON: { codes, data: { type: 'Buffer', data: number[] }, message }.
      // Do NOT use responseType: 'arraybuffer' — that would save the JSON text as a file and corrupt XLSX.
      const res = await getAxiosInstance(
        'adminOrchestrator/dashboard/getConsentLogsExcelByBusinessPartner',
        'post',
        { businessPartnerId: Number(id) },
        { 'journey-type': 'Dashboard' }
      );

      if (res?.data?.codes !== 200) {
        toast.error(res?.data?.message || 'Download failed');
        return;
      }

      const payload = res?.data?.data;
      let uint8Data = null;

      if (payload?.type === 'Buffer' && Array.isArray(payload?.data)) {
        uint8Data = new Uint8Array(payload.data);
      } else if (payload?.data?.type === 'Buffer' && Array.isArray(payload?.data?.data)) {
        uint8Data = new Uint8Array(payload.data.data);
      } else if (Array.isArray(payload)) {
        uint8Data = new Uint8Array(payload);
      }

      if (!uint8Data || uint8Data.byteLength === 0) {
        toast.error('Failed to download: Empty file');
        return;
      }

      // XLSX is a ZIP archive; must start with PK (0x50 0x4b).
      if (uint8Data[0] !== 0x50 || uint8Data[1] !== 0x4b) {
        toast.error('Invalid file from server (expected Excel)');
        return;
      }

      const blob = new Blob([uint8Data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'consent-logs.xlsx');

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Download successful!');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error downloading file');
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <>
      <Stack
        direction="row"
        flexWrap="wrap"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="h3">Logs</Typography>

        <Stack
          direction="row"
          flexWrap="wrap"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
        >
          {!showProduct && (
            <Button
              color="secondary"
              variant="contained"
              onClick={handleDownloadExcel}
              disabled={downloadLoading}
            >
              {downloadLoading ? 'Downloading...' : 'Download Excel'}
            </Button>
          )}
          <Autocomplete
            id="log-type-autocomplete"
            options={statusType}
            getOptionLabel={(option) => option.label}
            value={selectedLogType || ''}
            onChange={(event, newValue) => handleFilterChange('status', newValue)}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" />
            )}
            loading={filterLoading}
          />
        </Stack>
      </Stack>

      <Card sx={{ mt: 2 }}>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              height: '100px',
              alignItems: 'center',
            }}
          >
            <CircularProgress color="secondary" />
          </Box>
        ) : (
          <TableContainer>
            <Table
              sx={{ minWidth: 750, backgroundColor: 'white' }}
              aria-labelledby="tableTitle"
              size={dense ? 'small' : 'medium'}
            >
              <EnhancedTableHead
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                rowCount={total}
                showProduct={showProduct}
              />
              <TableBody>
                {logs.length ? (
                  <>
                    {logs.map((row, index) => {
                      if (typeof row === 'number') return null;
                      const isItemSelected = isSelected(row.name);
                      const labelId = `enhanced-table-checkbox-${index}`;

                      return (
                        <TableRow
                          hover
                          onClick={(event) => handleClick(event, row.name)}
                          role="checkbox"
                          aria-checked={isItemSelected}
                          tabIndex={-1}
                          key={row.name}
                          selected={isItemSelected}
                        >
                          <StyledTableCell component="th" id={labelId} scope="row">
                            {row?.referenceId}
                          </StyledTableCell>
                          {showProduct ? (
                            <StyledTableCell align="right">
                              {row?.product || 'N/A'}
                            </StyledTableCell>
                          ) : (
                            <StyledTableCell align="right">
                              {row?.fundType || 'N/A'}
                            </StyledTableCell>
                          )}
                          <StyledTableCell align="right">
                            {row?.uploadType || 'N/A'}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {row?.bankName}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {moment(row?.createdAt).format('MMM DD, YYYY hh:mm A')}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {row?.accountNumber
                              ? `******${row.accountNumber.slice(-4)}`
                              : '****'}
                          </StyledTableCell>
                          <StyledTableCell>
                            <Chip
                              size="small"
                              label={row.rejectionReason ? (
                                <span>
                                  {row.status}
                                  <br />
                                  {getRejectionReasonLabel(row.rejectionReason)}
                                </span>
                              ) : row.status}
                              sx={{
                                borderRadius: '4px',
                                color: colorChip[row.status]?.color,
                                background: colorChip[row.status]?.background,
                                textTransform: 'capitalize',
                                whiteSpace: 'pre-line',
                                minHeight: row.rejectionReason ? '40px' : '32px',
                                height: 'auto',
                                display: 'flex',
                                alignItems: 'center',
                                paddingTop: row.rejectionReason ? '6px' : '0',
                                paddingBottom: row.rejectionReason ? '6px' : '0',
                              }}
                            />
                          </StyledTableCell>
                        </TableRow>
                      );
                    })}
                  </>
                ) : (
                  <></>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
          component="div"
          count={total}
          rowsPerPage={limit}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>
    </>
  );
};

export default Logs;